import { supabase } from '@/core/database/supabase';
import type { Company, UsageStats, AuditLog } from '../types/company.types';

export const CompanyService = {
  async getCompany(companyId: string): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', companyId)
      .single();

    if (error) throw error;
    return data;
  },

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .update(updates)
      .eq('id', companyId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getUsageStats(companyId: string): Promise<UsageStats> {
    const [projects, users, assets, company] = await Promise.all([
      supabase.from('projects').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
      supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
      supabase.from('landscape_assets').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
      this.getCompany(companyId)
    ]);

    return {
      projects_count: projects.count || 0,
      users_count: users.count || 0,
      assets_count: assets.count || 0,
      storage_bytes: 0,
      max_projects: company.max_projects || 5,
      max_users: company.max_users || 10,
      max_assets: company.max_assets || 100
    };
  },

  async getAuditLogs(companyId: string, projectId?: string): Promise<AuditLog[]> {
    let query = supabase
      .from('audit_logs')
      .select(`
        *,
        actor:user_profiles!actor_user_id (
          full_name,
          email,
          avatar_path
        )
      `)
      .eq('company_id', companyId);

    if (projectId) {
      query = query.eq('project_id', projectId);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;

    return (data || []).map(log => ({
      ...log,
      actor_name: log.actor?.full_name,
      actor_email: log.actor?.email,
      actor_avatar: log.actor?.avatar_path
    }));
  }
};
